import React, { useMemo, useState } from 'react';
import {
    DndContext,
    DragOverlay,
    PointerSensor,
    KeyboardSensor,
    closestCenter,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { GripVertical } from 'tabler-icons-react';
import { useLlamadas, COLORES } from './LlamadasContext';
import ColorAccordion from './ColorAccordion';

function escapeRegExp(value) {
    return value.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
}

function LlamadasAcordeon() {
    const { llamadasPorColor, moverLlamada, expandedColors, toggleColor, searchText } =
        useLlamadas();
    const [activeLlamada, setActiveLlamada] = useState(null);
    const hayBusqueda = !!searchText;

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const gruposVisibles = useMemo(() => {
        if (!hayBusqueda) return llamadasPorColor;
        const re = new RegExp(escapeRegExp(searchText), 'i');
        return COLORES.reduce((acc, { slug }) => {
            acc[slug] = (llamadasPorColor[slug] || []).filter((llamada) => re.test(llamada.nombre));
            return acc;
        }, {});
    }, [llamadasPorColor, searchText, hayBusqueda]);

    const findColorOf = (id) =>
        COLORES.find((color) =>
            (llamadasPorColor[color.slug] || []).some((l) => l.tipoLlamada === id)
        );

    const handleDragStart = ({ active }) => {
        const color = findColorOf(active.id);
        const llamada = (llamadasPorColor[color?.slug] || []).find(
            (l) => l.tipoLlamada === active.id
        );
        setActiveLlamada(llamada || null);
    };

    const handleDragEnd = ({ active, over }) => {
        setActiveLlamada(null);
        if (!over || active.id === over.id) return;

        const colorActivo = findColorOf(active.id);
        const colorDestino = findColorOf(over.id);
        if (!colorActivo || !colorDestino || colorActivo.slug !== colorDestino.slug) return;

        const grupo = llamadasPorColor[colorActivo.slug] || [];
        const fromIndex = grupo.findIndex((l) => l.tipoLlamada === active.id);
        const toIndex = grupo.findIndex((l) => l.tipoLlamada === over.id);
        if (fromIndex === -1 || toIndex === -1) return;

        moverLlamada(colorActivo.slug, fromIndex, toIndex);
    };

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragCancel={() => setActiveLlamada(null)}
        >
            <div className="llam-acordeon">
                {COLORES.map((color) => {
                    const llamadas = gruposVisibles[color.slug] || [];
                    const expanded = hayBusqueda
                        ? llamadas.length > 0
                        : !!expandedColors[color.slug];
                    return (
                        <ColorAccordion
                            key={color.slug}
                            color={color}
                            llamadas={llamadas}
                            expanded={expanded}
                            onToggle={() => toggleColor(color.slug)}
                            dragDisabled={hayBusqueda}
                        />
                    );
                })}
            </div>

            <DragOverlay>
                {activeLlamada && (
                    <div className="llam-card llam-card--overlay">
                        <span className="llam-card-handle">
                            <GripVertical size={18} />
                        </span>
                        <div className="llam-card-info">
                            <span className="llam-card-name">{activeLlamada.nombre}</span>
                        </div>
                    </div>
                )}
            </DragOverlay>
        </DndContext>
    );
}

export default LlamadasAcordeon;
